import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import type {
  OnInit
} from '@angular/core';
import type { ControlValueAccessor } from '@angular/forms';

// Increasing integer for generating unique ids for checkbox components.
let nextUniqueId = 0;

/** Change event object emitted by PplCheckbox. */
export class PplCheckboxChange {
  /** The source PplCheckbox of the event. */
  source: PplCheckboxComponent;
  /** The new `checked` value of the checkbox. */
  checked: boolean;
  /** The new `indeterminate` state of the checkbox. */
  indeterminate: boolean;
}

@Component({
  selector: 'ppl-checkbox',
  templateUrl: 'checkbox.component.html',
  styleUrls: ['checkbox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'pplCheckbox',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PplCheckboxComponent),
      multi: true
    }
  ],
  standalone: false
})
export class PplCheckboxComponent implements OnInit, ControlValueAccessor {
  /** A unique id for the checkbox input. If none is supplied, it will be auto-generated. */
  @Input() id: string;

  @Input() readonly: boolean;

  @HostBinding('class.autofill') @Input() autofill: boolean;

  /** Whether the checkbox is required. */
  @Input()
  get required(): boolean {
    return this._required;
  }

  set required(value) {
    this._required = value;
  }

  /** Tabindex value that is passed to the underlying input element. */
  @Input() tabIndex = 0;

  /** Name value will be applied to the input element if present */
  @Input() name: string | null = null;

  @Input() disabled = false;

  @Input() disableFocus = false;

  @Input() block = false;

  /**
   * Whether the checkbox is checked.
   */
  @Input()
  get value() {
    return this._value;
  }

  set value(value: boolean) {
    if (value !== this.value) {
      this._value = value;
      this._changeDetectorRef.markForCheck();
    }
  }

  /**
   * Whether the checkbox is in indeterminate state.
   */
  @Input()
  get indeterminate() {
    return this._indeterminate;
  }

  set indeterminate(indeterminate: boolean) {
    if (indeterminate !== this.indeterminate) {
      this._indeterminate = indeterminate;
      this._changeDetectorRef.markForCheck();
    }
  }

  /** Event emitted when the checkbox's `checked` value changes. */
  @Output() valueChange: EventEmitter<PplCheckboxChange> = new EventEmitter<PplCheckboxChange>();

  /** Returns the unique id for the visual hidden input. */
  get inputId(): string {
    return `${this.id || this._uniqueId}-input`;
  }

  /** The native `<input type="checkbox"> element */
  @ViewChild('input', { static: true }) _inputElement: ElementRef;

  @ViewChild('check', { static: true }) check: ElementRef;

  checkFocus = false;

  private _uniqueId = `ppl-checkbox-${++nextUniqueId}`;
  private _required: boolean;
  private _value = false;
  private _indeterminate = false;

  constructor(private _changeDetectorRef: ChangeDetectorRef) { }

  ngOnInit() {
    if (!this.id) {
      this.id = this._uniqueId;
    }
  }

  /** Method being called whenever the label text changes. */
  _onLabelTextChange() {
    // This method is getting called whenever the label of the checkbox changes.
    // Since the checkbox uses the OnPush strategy we need to notify it about the change
    // that has been recognized by the cdkObserveContent directive.
    this._changeDetectorRef.markForCheck();
  }

  /** Function to call when the checkbox value changes. */
  onChange = (value: boolean) => { };

  onTouched = () => { };

  /**
   * Sets the model value. Implemented as part of ControlValueAccessor.
   * @param value Value to be set to the model.
   */
  writeValue(value: any) {
    this.value = !!value;
    this._changeDetectorRef.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Sets the checkbox's disabled state. Implemented as a part of ControlValueAccessor.
   * @param isDisabled Whether the checkbox should be disabled.
   */
  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
    this._changeDetectorRef.markForCheck();
  }

  /** Toggles the `checked` state of the checkbox. */
  toggle(): void {
    if (this.disabled || this.readonly) {
      return;
    }
    this.value = !this.value;
    if (this.onChange) {
      this.onChange(this.value);
    }
    this._emitChangeEvent();
  }

  /**
   * Event handler for checkbox input element.
   * Toggles checked state if element is not disabled.
   * Do not toggle on (valueChange) event since IE doesn't fire change event when
   *   indeterminate checkbox is clicked.
   * @param event
   */
  _onInputClick(event: Event) {
    // We have to stop propagation for click events on the visual hidden input element.
    // By default, when a user clicks on a label element, a generated click event will be
    // dispatched on the associated input element. Since we are using a label element as our
    // root container, the click event on the `checkbox` will be executed twice.
    // The real click event will bubble up, and the generated click event also tries to bubble up.
    // This will lead to multiple click events.
    // Preventing bubbling for the second event will solve that issue.
    event.stopPropagation();
    this.toggle();

    if (!this.disableFocus) {
      this.check.nativeElement.focus();
    }

    // Emit our custom change event if the native input emitted one.
    // It is important to only emit it, if the native input triggered one, because
    // we don't want to trigger a change event, when the `checked` variable changes for example.
  }

  _onInteractionEvent(event: Event) {
    // We always have to stop propagation on the change event.
    // Otherwise the change event, from the input element, will bubble up and
    // emit its event object to the `change` output.
    event.stopPropagation();
  }

  onCheckboxKeyDown($event: KeyboardEvent) {
    if (this.checkFocus && $event.keyCode === keyCodeSpace) {
      // if SPACE pressed
      $event.preventDefault();
      this.toggle();
      return;
    }
  }

  private _emitChangeEvent() {
    const event = new PplCheckboxChange();
    event.source = this;
    event.checked = this.value;

    this.valueChange.emit(event);
  }
}

const keyCodeSpace = 32;
